<template>
  <fieldset ref="metricKpiInfoSection" class="table-dynamic bs3-row-reset bs3-margin-bottom dropdown-menu-position-inline">
    <legend class="panel-section bs3-margin-bottom-reset">
      <div class="row">
        <div class="col-xs-3 text-left">
          <i class="fa-light fa-analytics"></i>
          <span class="title bs3-margin-left3">{{ 'dictionary.media.insertion.kpi.' + kpiName | i18nText(pDictionary) }}</span>
        </div>
        <div class="col-xs-6 text-center">
          <div v-if="timelinePeriod">
            <button class="btn btn-default btn-sm bs3-margin-right"
                    :disabled="!previousTimelinePeriodStartDateText"
                    @click="onPreviousTimelinePeriod"><i class="fa-light fa-chevron-left"></i></button>

            <div class="dropdown d-inline-block">
              <button class="btn btn-default dropdown-toggle" type="button" id="timelineIncrementsDropdown"
                      data-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                {{ timelinePeriod.name }} <span class="caret"></span>
              </button>
              <ul class="dropdown-menu" aria-labelledby="timelineIncrementsDropdown" style="max-height: 400px;">
                <li v-for="timelineIncrement in timelineIncrements" :key="timelineIncrement.startDateText"
                    :class="{'disabled': timelineIncrement.startDateText === currentTimelinePeriodStartDateText}">
                  <a href="#" @click.prevent="onTimelinePeriodChange(timelineIncrement.startDateText)">{{ timelineIncrement.name }}</a></li>
              </ul>
            </div>

            <button class="btn btn-default btn-sm bs3-margin-left"
                    :disabled="!nextTimelinePeriodStartDateText"
                    @click="onNextTimelinePeriod"><i class="fa-light fa-chevron-right"></i></button>
          </div>
        </div>
        <div class="col-xs-3 text-right">
          <div class="btn-group" role="group" aria-label="Display timeline by">
            <a ref="calendarTimelineDay"
               :class="['btn btn-sm', isTimelineByActive('DAY') ? 'btn-primary active': 'btn-default']"
               data-toggle="tooltip" :title="pDictionary['dictionary.general.date.day']"
               @click.prevent="onTimelineChange('DAY')">{{ 'dictionary.general.date.day.short' | i18nText(pDictionary) }}
            </a>
            <a ref="calendarTimelineWeek"
               :class="['btn btn-sm', isTimelineByActive('WEEK') ? 'btn-primary active': 'btn-default']"
               data-toggle="tooltip" :title="'dictionary.general.date.week' | i18nText(pDictionary)"
               @click.prevent="onTimelineChange('WEEK')">{{ 'dictionary.general.date.week.short' | i18nText(pDictionary) }}
            </a>
            <a ref="calendarTimelineMonth"
               :class="['btn btn-sm', isTimelineByActive('MONTH') ? 'btn-primary active': 'btn-default']"
               data-toggle="tooltip" :title="'dictionary.general.date.month' | i18nText(pDictionary)"
               @click.prevent="onTimelineChange('MONTH')">{{ 'dictionary.general.date.month.short' | i18nText(pDictionary) }}
            </a>
            <a ref="calendarTimelineQuarter"
               :class="['btn btn-sm', isTimelineByActive('QUARTER') ? 'btn-primary active': 'btn-default']"
               data-toggle="tooltip" :title="'dictionary.general.date.quarter' | i18nText(pDictionary)"
               @click.prevent="onTimelineChange('QUARTER')">{{ 'dictionary.general.date.quarter.short' | i18nText(pDictionary) }}
            </a>
            <a ref="calendarTimelineYear"
               :class="['btn btn-sm', isTimelineByActive('YEAR') ? 'btn-primary active': 'btn-default']"
               data-toggle="tooltip" :title="'dictionary.general.date.year' | i18nText(pDictionary)"
               @click.prevent="onTimelineChange('YEAR')">{{ 'dictionary.general.date.year.short' | i18nText(pDictionary) }}
            </a>
          </div>
        </div>
      </div>
    </legend>
    <div class="viewDetails bs3-padding content-scrollable mh500">
      <div v-if="loading" class="text-center text-muted">
        <i class="fa-light fa-3x fa-spinner fa-pulse fa-fw"></i>
      </div>
      <div v-else>
        <div class="row">
          <div class="col-xs-6 period">
            <metric-kpi-info-summary
                :p-dictionary="pDictionary"
                :p-currencies="pCurrencies"
                :p-kpi-name="kpiName"
                :p-data-list-input="periodDataListInput"
                p-type="PERIOD">
            </metric-kpi-info-summary>
          </div>
          <div class="col-xs-6 total">
            <metric-kpi-info-summary
                :p-dictionary="pDictionary"
                :p-currencies="pCurrencies"
                :p-kpi-name="kpiName"
                :p-data-list-input="pData.dataListInput"
                p-type="TOTAL">
            </metric-kpi-info-summary>
          </div>
        </div>
        <ul class="nav nav-tabs nav-tabs-sm" role="tablist">
          <li role="presentation" class="active">
            <a href="#kpiChart" aria-controls="kpiChart" role="tab" data-toggle="tab">{{ 'dictionary.media.insertion.kpi.chart' | i18nText(pDictionary) }}</a>
          </li>
          <li role="presentation">
            <a href="#kpiSheet" aria-controls="kpiSheet" role="tab" data-toggle="tab">{{ 'dictionary.media.insertion.kpi.sheet' | i18nText(pDictionary) }}</a>
          </li>
          <div class="pull-right">
            <a v-if="updateActualButtonVisible"
               :href="'/mediaplan/secure/planification/bulk.action?bulkCmdDataForm.actionName=MediaInsertionMetricKpiActualUpdate' + urlParams"
               class="btn btn-default btn-sm ajax-support show-waiting" data-ajax-legacy="true" data-toggle="link">
              <i class="fa-light fa-edit"></i> {{ "dictionary.media.insertion.metric.kpi.actual.cmd.update" | i18nText(pDictionary) }}</a>
          </div>
        </ul>
        <div class="tab-content">
          <div role="tabpanel" class="tab-pane active" id="kpiChart">
            <metric-kpi-info-chart-section
                :p-currencies="pCurrencies"
                :p-language="pLanguage"
                :p-dictionary="pDictionary"
                :p-kpi-name="kpiName"
                :p-data-list-input="pData.dataListInput"
                :p-calendar-timeline="calendarTimeline"
                :p-timeline-period-start-date-text="timelinePeriod.startDateText">
            </metric-kpi-info-chart-section>
          </div>
          <div role="tabpanel" class="tab-pane" id="kpiSheet">
            <metric-kpi-info-sheet-section
                :p-currencies="pCurrencies"
                :p-language="pLanguage"
                :p-dictionary="pDictionary"
                :p-kpi-name="kpiName"
                :p-data-list-input="pData.dataListInput">
            </metric-kpi-info-sheet-section>
          </div>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script>
import FormatMixin from "@/mixins/format-mixin";
import AnalysisLevelMixin from "@/components/common/metric/kpi/analysis-level-mixin";
import MetricKpiInfoChartSection from "@/components/common/metric/kpi/info/MetricKpiInfoChartSection";
import MetricKpiInfoSummary from "@/components/common/metric/kpi/info/MetricKpiInfoSummary";
import MetricKpiInfoSheetSection from "@/components/common/metric/kpi/info/MetricKpiInfoSheetSection";
import refHelpers from "@/helpers/ref-helpers";

export default {
  name: "MetricKpiInfoSection",
  components: {MetricKpiInfoSheetSection, MetricKpiInfoSummary, MetricKpiInfoChartSection},
  mixins: [FormatMixin, AnalysisLevelMixin],
  props: {
    pCurrencies: Array,
    pLanguage: String,
    pDictionary: {
      type: Object,
      required: true
    },
    pSrc: {
      type: String,
      default: function () {
        return ''
      }
    },
    pSrcId: {
      type: String,
      default: function () {
        return ''
      }
    },
    pData: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      loading: true,
      calendarTimeline: 'DAY',
      timelinePeriod: null,
      timelineIncrements: [],
      previousTimelinePeriodStartDateText: null,
      currentTimelinePeriodStartDateText: null,
      nextTimelinePeriodStartDateText: null,
      periodDataListInput: null,
    }
  },
  methods: {
    isTimelineByActive(calendarTimeline)
    {
      return this.calendarTimeline === calendarTimeline;
    },
    onTimelineChange(calendarTimeline)
    {
      this.calendarTimeline = calendarTimeline;
      this.refreshTimelinePeriod(this.timelinePeriod.startDateText);
    },
    onPreviousTimelinePeriod()
    {
      this.refreshTimelinePeriod(this.previousTimelinePeriodStartDateText);
    },
    onNextTimelinePeriod()
    {
      this.refreshTimelinePeriod(this.nextTimelinePeriodStartDateText);
    },
    onTimelinePeriodChange(startDateText)
    {
      this.refreshTimelinePeriod(startDateText);
    },
    refreshTimelinePeriod(timelinePeriodStartDateText)
    {
      let _component = this;
      _component.loading = true;
      $.ajax({
               type: "POST",
               url: "/mediaplan/app/endpoints/json-api/metrics/kpis/timeline/period?" +
                    "calendarTimeline=" + _component.calendarTimeline +
                    "&timelinePeriodStartDateText=" + (timelinePeriodStartDateText || ''),
               data: JSON.stringify(_component.pData.dataListInput),
               headers: {
                 'Accept': 'application/json',
                 'Content-Type': 'application/json'
               }
             }).done(function (data) {
        _component.timelinePeriod = data.timelinePeriod;
        _component.timelineIncrements = data.timelineIncrements;

        _component.previousTimelinePeriodStartDateText = data.previousTimelinePeriodStartDateText;
        _component.currentTimelinePeriodStartDateText = data.timelinePeriod.startDateText;
        _component.nextTimelinePeriodStartDateText = data.nextTimelinePeriodStartDateText;

        _component.periodDataListInput = Object.assign({}, _component.pData.dataListInput);
        _component.periodDataListInput.startDateText = _component.timelinePeriod.startDateText;
        _component.periodDataListInput.endDateText = _component.timelinePeriod.endDateText;

        _component.loading = false;
      });
    }
  },
  computed: {
    kpiName: function() {
      return this.pData.name.toLowerCase();
    },
    updateActualButtonVisible: function () {
      return this.pData.allowedToUpdate && this.mediaInsertionRefId != null;
    },
    mediaInsertionRefId: function () {
      if(refHelpers.isValidRef(this.pData.mediaInsertionRef))
      {
        return this.pData.mediaInsertionRef.id;
      }
      return null;
    },
    urlParams: function () {
      let urlParams = "";
      if(this.pSrc)
      {
        urlParams += "&src=" + this.pSrc;
      }
      if(this.pSrcId)
      {
        urlParams += "&srcId=" + this.pSrcId;
      }
      if(this.mediaInsertionRefId)
      {
        urlParams += "&bulkCmdDataForm.ids=" + this.mediaInsertionRefId;
      }
      else
      {
        urlParams += "&bulkCmdDataForm.all=true"
      }
      return urlParams;
    }
  },
  mounted: function () {
    const _component = this;
    _component.$nextTick(function () {
      const $metricKpiInfoSection = $(_component.$refs.metricKpiInfoSection);
      $metricKpiInfoSection.find('[data-toggle="tooltip"]').tooltip({sanitize: false, container: 'body'});
    });

    _component.refreshTimelinePeriod(null);
  },
  updated: function () {
    const _component = this;
    _component.$nextTick(function () {
      const $metricKpiInfoSection = $(_component.$refs.metricKpiInfoSection);
      $metricKpiInfoSection.find('[data-toggle="tooltip"]').tooltip({sanitize: false, container: 'body'});
    });
  },
  destroy: function () {
    const _component = this;
    const $metricKpiInfoSection = $(_component.$refs.metricKpiInfoSection);
    $metricKpiInfoSection.find('[data-toggle="tooltip"]').tooltip('destroy');
  }
}
</script>

<style scoped>

</style>